import { PATCH_MAX_INSTRUMENT_HISTORY_YEARS } from "@repo/shared";
import type { InstrumentMetadata } from "@repo/shared/types";
import { Pencil, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { TableCell, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { post } from "@/lib/api/_client";
import { isWithinLastNYears } from "@/lib/date-utils";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./combined-instrument-allocation-row.i18n";

export type InstrumentWithDetails = {
  instrumentId: string;
  weight: number;
  patchInstrumentId?: string | null;
  /** QL-332 — raw series (true) or converted into the basket currency (false). */
  hedged: boolean;
  displayName: string;
  displayTicker: string;
  displayDataset: string;
  patchDisplayName?: string | null;
  patchTicker?: string | null;
  patchDataset?: string | null;
  /** Native currencies, filled in from the pick and refreshed by the fetch below. */
  currency?: string | null;
  patchCurrency?: string | null;
  patchFirstDate?: string | null;
  patchLastDate?: string | null;
  mainInstrumentFirstDate: string;
  mainInstrumentLastDate: string;
};

type CombinedInstrumentAllocationRowProps = {
  item: InstrumentWithDetails;
  index: number;
  isEditMode?: boolean;
  onWeightChange: (index: number, value: string) => void;
  onHedgedChange: (index: number, hedged: boolean) => void;
  /** The basket's currency, so the row can say whether this leg will be converted. */
  basketCurrency: string | null;
  onRemove: (index: number) => void;
  onPatchSelect: (item: InstrumentWithDetails) => void;
  onPatchRemove: (item: InstrumentWithDetails) => void;
  isPending?: boolean;
  /** The server's reason for refusing THIS leg's patch, after a rejected save. */
  patchError?: string | null;
};

export function CombinedInstrumentAllocationRow({
  item,
  index,
  isEditMode,
  onWeightChange,
  onHedgedChange,
  basketCurrency,
  onRemove,
  onPatchSelect,
  onPatchRemove,
  isPending,
  patchError,
}: CombinedInstrumentAllocationRowProps) {
  const { formatDate } = useRegionalFormat();
  const t = useT(T);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [resolvedName, setResolvedName] = useState<string | null>(null);
  const [resolvedPatchName, setResolvedPatchName] = useState<string | null>(null);
  const [resolvedPatchTicker, setResolvedPatchTicker] = useState<string | null>(
    item.patchTicker ?? null,
  );
  const [resolvedPatchDataset, setResolvedPatchDataset] = useState<string | null>(
    item.patchDataset ?? null,
  );
  const [resolvedPatchFirstDate, setResolvedPatchFirstDate] = useState<string | null>(
    item.patchFirstDate ?? null,
  );
  const [resolvedPatchLastDate, setResolvedPatchLastDate] = useState<string | null>(
    item.patchLastDate ?? null,
  );
  const [resolvedCurrency, setResolvedCurrency] = useState<string | null>(item.currency ?? null);
  const [resolvedPatchCurrency, setResolvedPatchCurrency] = useState<string | null>(
    item.patchCurrency ?? null,
  );
  const [resolvedFirstDate, setResolvedFirstDate] = useState<string>(item.mainInstrumentFirstDate);
  const [resolvedLastDate, setResolvedLastDate] = useState<string>(item.mainInstrumentLastDate);

  useEffect(() => {
    if (!isEditMode) return;

    // The picker can change the instrument or its patch while a fetch is still in
    // flight. Without this flag the response that lands LAST wins rather than the
    // one that was asked for last, so an older reply could overwrite the newer one.
    let cancelled = false;

    setIsLoadingMetadata(true);

    const fetchMetadata = async () => {
      const [dataset, ticker] = item.instrumentId.split("+");

      const fetchMeta = async (t: string, ds: string) => {
        try {
          return await post<InstrumentMetadata>(
            `/internal/portfolio-collections/products/instrument-metadata`,
            { ticker: t, dataset: ds },
          );
        } catch {
          return null;
        }
      };

      const [instrumentData, patchData] = await Promise.all([
        fetchMeta(ticker, dataset),
        item.patchInstrumentId
          ? (() => {
              const [patchDataset, patchTicker] = item.patchInstrumentId!.split("+");
              return fetchMeta(patchTicker, patchDataset);
            })()
          : Promise.resolve(null),
      ]);

      if (cancelled) return;

      // A lookup that failed, or a patch that was cleared, must fall back to what
      // the item itself carries — never leave the previous instrument's metadata on
      // screen as if it belonged to this one.
      if (instrumentData) {
        setResolvedName(instrumentData.name ?? null);
        setResolvedCurrency(instrumentData.currency ?? null);
        setResolvedFirstDate(instrumentData.firstdate ?? "");
        setResolvedLastDate(instrumentData.lastdate ?? "");
      } else {
        setResolvedName(null);
        setResolvedCurrency(item.currency ?? null);
        setResolvedFirstDate(item.mainInstrumentFirstDate);
        setResolvedLastDate(item.mainInstrumentLastDate);
      }

      if (patchData) {
        setResolvedPatchName(patchData.name ?? null);
        setResolvedPatchCurrency(patchData.currency ?? null);
        setResolvedPatchTicker(patchData.ticker ?? null);
        setResolvedPatchDataset(item.patchInstrumentId!.split("+")[0]);
        setResolvedPatchFirstDate(patchData.firstdate ?? null);
        setResolvedPatchLastDate(patchData.lastdate ?? null);
      } else {
        setResolvedPatchName(null);
        setResolvedPatchCurrency(item.patchCurrency ?? null);
        setResolvedPatchTicker(item.patchTicker ?? null);
        setResolvedPatchDataset(item.patchDataset ?? null);
        setResolvedPatchFirstDate(item.patchFirstDate ?? null);
        setResolvedPatchLastDate(item.patchLastDate ?? null);
      }

      setIsLoadingMetadata(false);
    };

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [isEditMode, item.instrumentId, item.patchInstrumentId]);

  // The server refuses a patch whose NATIVE currency differs from that of the
  // instrument it patches, and that is much the most common rejection. Both
  // currencies are already in hand — the effect above fetches them, and a fresh
  // pick carries them on the item — so say so where the patch is chosen rather
  // than after submitting. The server stays authoritative: this never blocks the
  // save, it only explains what is about to happen.
  const currency = resolvedCurrency ?? item.currency ?? null;
  const patchCurrency = resolvedPatchCurrency ?? item.patchCurrency ?? null;
  const currencyMismatch = Boolean(
    item.patchInstrumentId && currency && patchCurrency && currency !== patchCurrency,
  );

  // The hedged switch only means something for a leg in another currency than
  // the basket: a same-currency leg is never converted either way. Unknown
  // currency (metadata still loading) keeps the switch enabled rather than
  // guessing.
  const sameCurrency = Boolean(currency && basketCurrency && currency === basketCurrency);

  // Mirrors the server's instrument_history_sufficient rule: a leg whose own
  // series already reaches back PATCH_MAX_INSTRUMENT_HISTORY_YEARS is not
  // offered a patch. Unknown first date is "cannot judge", not "long enough".
  const patchNotNeeded = Boolean(
    resolvedFirstDate && !isWithinLastNYears(resolvedFirstDate, PATCH_MAX_INSTRUMENT_HISTORY_YEARS),
  );

  // A leg that lands in the basket currency — the designer changed the basket
  // currency, or picked a same-currency instrument into a hedged row — drops
  // its hedge, so the disabled switch shows what the server stores: it
  // normalizes `hedged` to false for such a leg on every save.
  useEffect(() => {
    if (sameCurrency && item.hedged) onHedgedChange(index, false);
  }, [sameCurrency, item.hedged, index, onHedgedChange]);
  const treatment = !currency
    ? null
    : sameCurrency
      ? t.sameCurrency
      : item.hedged
        ? t.hedgedRaw(currency)
        : basketCurrency
          ? t.converted(currency, basketCurrency)
          : null;

  return (
    <TableRow>
      <TableCell>
        <div>
          <p className="font-medium">
            {isLoadingMetadata ? item.displayTicker : resolvedName || item.displayName}
          </p>
          <p className="text-xs text-muted-foreground">
            {item.displayTicker} ({item.displayDataset})
          </p>
          {treatment && <p className="text-xs text-muted-foreground">{treatment}</p>}
          {resolvedFirstDate && resolvedLastDate && (
            <p className="text-xs text-muted-foreground">
              {formatDate(resolvedFirstDate)} — {formatDate(resolvedLastDate)}
            </p>
          )}
        </div>
      </TableCell>
      <TableCell>
        {item.patchInstrumentId ? (
          <div>
            <div className="flex items-center gap-1">
              <p className="font-medium" title={item.patchInstrumentId}>
                {resolvedPatchName || item.patchDisplayName || item.patchInstrumentId}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={() => onPatchSelect(item)}
                disabled={isPending}
                tooltip={t.changePatch}
              >
                <Pencil className="h-3 w-3" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={() => onPatchRemove(item)}
                disabled={isPending}
                tooltip={t.removePatch}
              >
                <X className="h-3 w-3 text-destructive" />
              </Button>
            </div>
            {resolvedPatchTicker && resolvedPatchDataset && (
              <p className="text-xs text-muted-foreground">
                {resolvedPatchTicker} ({resolvedPatchDataset})
              </p>
            )}
            {resolvedPatchFirstDate && resolvedPatchLastDate && (
              <p className="text-xs text-muted-foreground">
                {formatDate(resolvedPatchFirstDate)} — {formatDate(resolvedPatchLastDate)}
              </p>
            )}
            {currencyMismatch && !patchError && (
              <p className="text-xs text-warning">
                {t.currencyMismatch(patchCurrency ?? "", currency ?? "")}
              </p>
            )}
            {patchError && <p className="text-xs text-destructive">{patchError}</p>}
          </div>
        ) : patchNotNeeded ? (
          // Always offered, disabled with the reason when the leg's own
          // history is long enough — the server refuses such a patch, so
          // say so here instead of hiding the button. The span carries the
          // hover: a disabled button has pointer-events-none, so its own
          // tooltip cannot.
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" disabled>
                  {t.addPatch}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t.patchNotNeeded(String(PATCH_MAX_INSTRUMENT_HISTORY_YEARS))}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onPatchSelect(item)}
            // Unknown first date (metadata still loading): hold the button
            // rather than offer a patch that may be refused.
            disabled={isPending || !resolvedFirstDate}
          >
            {t.addPatch}
          </Button>
        )}
      </TableCell>
      <TableCell>
        <Switch
          checked={item.hedged}
          onCheckedChange={(checked) => onHedgedChange(index, checked)}
          disabled={isPending || sameCurrency}
          aria-label={t.hedged}
          title={sameCurrency ? t.sameCurrency : undefined}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          min={0.01}
          max={100}
          step={0.01}
          value={item.weight}
          onChange={(e) => onWeightChange(index, e.target.value)}
          disabled={isPending}
          required
        />
      </TableCell>
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          tooltip={t.removeAllocation}
          onClick={() => onRemove(index)}
          disabled={isPending}
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
